// 📁 src/pages/BacktestDetailsPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/ui/Button';
import TradeHistoryTable from '@/components/TradeHistoryTable';
import { buildPairsForDetails, buildPairsForCard, normPairStat, formatPctFrac as pairFormatPctFrac, formatPairSymbolForQuote } from '@/utils/pairs';
import { formatPct, formatPctFrac, formatMoney } from '@/utils/format';
import { toNumOrNull } from '@/utils/num';
import { computeCardKpis } from '@/utils/kpi';
import { KPI_TOOLTIPS } from '@/utils/kpi-tooltips';
import { Tooltip } from '@/ui/Tooltip';
import { fetchBacktestDetails, fetchBacktestStats, fetchBacktestCardRow } from '@/lib/db/backtests';
import type { DetailsRowV2 } from '@/types/backtests';
import { ArrowLeft, TrendingUp, TrendingDown, BarChart3, Target, ChevronDown, ChevronUp, Percent, Info, AlertCircle, Clock, XCircle, Code } from 'lucide-react';

type Trade = {
  id: string;
  trade_time: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  qty: number;
  price: number;
  fee: number;
  pnl_usd: number;
  pnl_percent: number;
  created_at: string;
  exit_reason?: string;
  exit_strategy?: string;
  duration_minutes?: number;
};

function formatDurationStr(minutes: number): string {
  if (!minutes || minutes === 0) return 'N/A';
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;
  
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  
  return parts.join(' ');
}

type PairStats = {
  symbol: string; total_trades: number; winning_trades: number; losing_trades: number; win_rate: number;
  total_profit_pct: number; total_profit_usd: number; avg_profit_pct: number; best_trade_pct: number;
  worst_trade_pct: number; buy_orders: number; sell_orders: number; buy_wins: number; buy_losses: number;
  sell_wins: number; sell_losses: number;
};

// 🔥 3. IŠTAISYTA 16% TP STAGE KLAIDA: Nuskaito TIKRUS nustatymus, kad rodytų (pvz., 20%)
const calculateExitStageInfo = (
  explanation: string | null,
  expectedExit: string | null,
  takeProfitStages?: any[],
  actualSoldPct?: number,
  netUsd?: number
) => {
  let displayPct = actualSoldPct ?? 100;
  
  const stageMatch = explanation?.match(/stage\s*(\d+)/i) || expectedExit?.match(/TP_STAGE_(\d+)/i);

  if (stageMatch) {
    const stage = parseInt(stageMatch[1]);
    
    if (takeProfitStages && takeProfitStages.length >= stage) {
      const stageSetting = takeProfitStages[stage - 1];
      const configPct = stageSetting?.sell_pct || stageSetting?.sellPercentage || stageSetting?.buy_pct || stageSetting?.buyPercentage || stageSetting?.percentage;
      if (configPct) {
        displayPct = configPct > 1 ? configPct : configPct * 100;
      }
    }

    return {
      stage: stage,
      text: `TP STAGE ${stage}`,
      details: `Sold ${displayPct.toFixed(2)}%`,
      color: 'green' as const
    };
  }

  const isStopLoss = explanation?.toLowerCase().includes('stop loss') || 
                     explanation?.toLowerCase().includes('stop_loss') || 
                     expectedExit === 'STOP_LOSS';

  const isTrailingStop = explanation?.toLowerCase().includes('trailing stop') || 
                         expectedExit === 'TRAILING_STOP';

  if (isStopLoss) {
    return { stage: null, text: 'STOP LOSS', details: `Sold ${displayPct.toFixed(2)}%`, color: 'red' as const };
  }

  if (isTrailingStop) {
    return { stage: null, text: 'TRAILING STOP', details: `Sold ${displayPct.toFixed(2)}%`, color: 'blue' as const };
  }

  if (explanation && explanation !== 'Strategy execution') {
    return { stage: null, text: explanation, details: `Sold ${displayPct.toFixed(2)}%`, color: 'gray' as const };
  }

  if (netUsd !== undefined && netUsd > 0) {
    return { stage: null, text: 'TAKE PROFIT', details: `Sold ${displayPct.toFixed(2)}%`, color: 'green' as const };
  } else {
    return { stage: null, text: 'STRATEGY EXIT', details: `Sold ${displayPct.toFixed(2)}%`, color: 'gray' as const };
  }
};

const getPositionTypeFromSide = (side: string, position_type?: string): string => {
  if (position_type) return position_type;
  const upperSide = (side || '').toUpperCase();
  if (['BUY', 'SELL', 'SELL_TP', 'SELL_SL'].includes(upperSide)) return 'LONG';
  if (['SHORT', 'BUY_TP', 'BUY_SL'].includes(upperSide)) return 'SHORT';
  return 'LONG';
};

const transformTradesForFrontend = (trades: any[], takeProfitStages?: any[]) => {
  const sortedTrades = [...trades].sort((a, b) => new Date(a.trade_time).getTime() - new Date(b.trade_time).getTime());
  const tradePairs: { [key: string]: any[] } = {};
  const transformedTrades = [];

  for (const trade of sortedTrades) {
    const symbol = trade.symbol;
    const positionType = getPositionTypeFromSide(trade.side, trade.position_type);
    const isEntry = (trade.side === 'BUY' && positionType === 'LONG') ||
                    (trade.side === 'SHORT' && positionType === 'SHORT');

    if (isEntry) {
      if (!tradePairs[symbol]) tradePairs[symbol] = [];
      tradePairs[symbol].push({
        ...trade,
        position_type: positionType,
        open_time: trade.trade_time,
        open_price: trade.price,
        original_qty: trade.qty,
        current_qty: trade.qty
      });
    } else {
      const hasEntryTrade = tradePairs[symbol] && tradePairs[symbol].length > 0;
      const entryTrade = hasEntryTrade ? tradePairs[symbol][0] : null;

      let soldPct = 100;
      let durationSeconds = 0;
      let durationFormatted = '-';

      if (entryTrade) {
        if (entryTrade.original_qty && entryTrade.original_qty > 0) {
          soldPct = (trade.qty / entryTrade.original_qty) * 100;
          if (soldPct > 100) soldPct = 100;
          entryTrade.current_qty -= trade.qty;
        }

        const openTime = new Date(entryTrade.open_time);
        const closeTime = new Date(trade.trade_time);
        durationSeconds = Math.floor((closeTime.getTime() - openTime.getTime()) / 1000);
        durationFormatted = formatDurationStr(Math.floor(durationSeconds / 60));

        if (!trade.exit_reason?.includes('STAGE') && !trade.explanation?.includes('STAGE')) {
           if (entryTrade.current_qty <= 0.000001) tradePairs[symbol].shift();
        } else {
           if (entryTrade.current_qty <= 0.000001) tradePairs[symbol].shift();
        }
      }

      const fee = trade.fee ?? trade.fees ?? 0;
      const gross = trade.profit_amount ?? trade.profitAmount ?? trade.profit ?? trade.pnl_usd ?? trade.profit_usd ?? 0;
      const netUsd = trade.net_profit !== undefined ? trade.net_profit : (gross - fee);

      const exitStageInfo = calculateExitStageInfo(
        trade.exit_reason || trade.explanation || null,
        trade.exit_strategy || trade.close_reason || null,
        takeProfitStages,
        trade.sold_pct ?? soldPct,
        netUsd
      );

      transformedTrades.push({
        ...trade,
        id: trade.id,
        symbol: trade.symbol,
        side: trade.side,
        position_type: positionType,
        entry_price: entryTrade?.open_price || null,
        exit_price: trade.price,
        price: trade.price,
        qty: trade.qty,
        quantity: trade.qty,
        trade_time: trade.trade_time,
        timestamp: trade.trade_time,
        opened_at: entryTrade?.open_time || trade.trade_time,
        closed_at: trade.trade_time,
        pnl_percent: trade.pnl_percent,
        pnl_usd: trade.pnl_usd,
        profit_pct: trade.pnl_percent,
        profit_usd: trade.pnl_usd,
        duration_seconds: durationSeconds,
        duration: trade.duration || durationFormatted,
        sold_pct: trade.sold_pct ?? soldPct,
        exit_stage: exitStageInfo,
        exit_reason: exitStageInfo.text,
        exit_stage_details: exitStageInfo.details,
        fee: trade.fee,
        fee_currency: 'USDC',
        position_size: entryTrade ? trade.qty * entryTrade.open_price : null,
        trade_type: 'backtest',
        original_trade: trade
      });
    }
  }

  for (const symbol in tradePairs) {
    for (const entryTrade of tradePairs[symbol]) {
      transformedTrades.push({
        ...entryTrade,
        id: entryTrade.id,
        side: entryTrade.side,
        position_type: entryTrade.position_type,
        entry_price: entryTrade.open_price,
        exit_price: null,
        price: entryTrade.open_price,
        qty: entryTrade.current_qty,
        quantity: entryTrade.current_qty,
        trade_time: entryTrade.trade_time,
        timestamp: entryTrade.trade_time,
        opened_at: entryTrade.trade_time,
        pnl_percent: null,
        pnl_usd: null,
        profit_pct: null,
        profit_usd: null,
        duration_seconds: null,
        duration_minutes: null,
        exit_stage: { text: 'Position Open', details: 'Still open at backtest end', color: 'blue' },
        exit_reason: 'Position Open',
        exit_stage_details: 'Still open at backtest end',
        fee: entryTrade.fee,
        fee_currency: 'USDC',
        position_size: entryTrade.current_qty * entryTrade.open_price,
        trade_type: 'backtest',
        original_trade: entryTrade
      });
    }
  }

  return transformedTrades.reverse();
};

function BacktestDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [activeTab, setActiveTab] = useState<'overview' | 'trades' | 'pairs'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [backtest, setBacktest] = useState<DetailsRowV2 | null>(null);
  const [cardRow, setCardRow] = useState<any>(null);
  const [trades, setTrades] = useState<any[]>([]);
  const [tradesSource, setTradesSource] = useState<string>('none');
  const [pairStats, setPairStats] = useState<PairStats[]>([]);
  const [expandedPair, setExpandedPair] = useState<string | null>(null);
  const [statsRow, setStatsRow] = useState<any>(null);

  const [calculatedStats, setCalculatedStats] = useState({
    totalProfitUsd: 0, totalProfitPct: 0, totalTrades: 0, winningTrades: 0, losingTrades: 0,
    winRate: 0, maxDrawdown: 0, sharpeRatio: 0, buyOrders: 0, sellOrders: 0, buyWins: 0,
    buyLosses: 0, sellWins: 0, sellLosses: 0, avgWin: 0, avgLoss: 0, profitFactor: 0,
    bestTrade: 0, worstTrade: 0, grossProfit: 0, grossLoss: 0,
  });

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);

        const { row: header } = await fetchBacktestDetails(id!);
        if (!header) throw new Error('Backtest not found');

        const cardRowData = await fetchBacktestCardRow(id!);
        const statsRowData = await fetchBacktestStats(id!);
        setStatsRow(statsRowData);

        let tradesRows: any[] = [];
        let source = 'none';

        const { data: backtestTradesData, error: backtestTradesError } = await supabase
          .from('backtest_trades')
          .select('*')
          .eq('backtest_id', id)
          .order('trade_time', { ascending: false });

        if (!backtestTradesError && backtestTradesData && backtestTradesData.length > 0) {
          
          let takeProfitStages: any[] = [];
          try {
            if (header?.settings_snapshot) {
              const settings = typeof header.settings_snapshot === 'string' ? JSON.parse(header.settings_snapshot) : header.settings_snapshot;
              takeProfitStages = settings?.sell_take_profit_stages || settings?.sellTakeProfitStages || settings?.take_profit_stages || settings?.takeProfitStages || [];
            } else if (header?.config_snapshot) {
              const settings = typeof header.config_snapshot === 'string' ? JSON.parse(header.config_snapshot) : header.config_snapshot;
              takeProfitStages = settings?.sell_take_profit_stages || settings?.take_profit_stages || [];
            }
          } catch (e) {}

          tradesRows = transformTradesForFrontend(backtestTradesData, takeProfitStages);
          source = 'backtest_trades (transformed)';
        } else {
          const { data: backtestWithTrades } = await supabase.from('backtest_results').select('trade_history, bot_id').eq('id', id).maybeSingle();
          if (backtestWithTrades) {
            if (Array.isArray(backtestWithTrades.trade_history)) {
              tradesRows = backtestWithTrades.trade_history;
              source = 'backtest_results.trade_history';
            }
            if (tradesRows.length === 0 && backtestWithTrades.bot_id) {
              const { data: tradeLogData } = await supabase.from('trade_log').select('*').eq('bot_id', backtestWithTrades.bot_id).order('timestamp', { ascending: false });
              if (tradeLogData && tradeLogData.length > 0) {
                tradesRows = tradeLogData;
                source = 'trade_log';
              }
            }
          }
        }

        setTradesSource(source);

        tradesRows = tradesRows.map((trade: any, idx: number) => ({
          ...trade,
          id: trade.id || `trade-${idx}`,
          side: (trade.side || trade.type || '').toUpperCase(),
          position_type: trade.position_type || trade.positionType || undefined,
          symbol: trade.symbol || trade.pair || '',
          price: trade.price || trade.entry_price || trade.entryPrice || 0,
          exit_price: trade.exit_price || trade.exitPrice || 0,
          qty: trade.qty || trade.quantity || trade.amount || 0,
          pnl_usd: trade.pnl_usd || trade.pnlUsd || trade.profit_usd || trade.profit_amount || 0,
          pnl_percent: trade.pnl_percent || trade.pnlPercent || trade.profit_percent || trade.profitPercent || trade.profit_percentage || trade.pnl_pct || 0,
          trade_time: trade.trade_time || trade.tradeTime || trade.timestamp || trade.date || trade.opened_at || trade.closed_at || new Date().toISOString(),
          duration_minutes: trade.duration_minutes || trade.durationMinutes || trade.duration || 0,
          duration_seconds: trade.duration_seconds || trade.durationSeconds || (trade.duration_minutes ? trade.duration_minutes * 60 : 0),
          duration: trade.duration,
          sold_pct: trade.sold_pct,
          exit_reason: trade.exit_reason || trade.exitReason || trade.reason || trade.status || trade.explanation || trade.close_reason || 'Strategy execution',
          fee: trade.fee || trade.commission || 0,
          fee_currency: trade.fee_currency || trade.feeCurrency || trade.commission_asset || trade.commissionAsset || 'USDC',
          trade_type: trade.trade_type || trade.tradeType || trade.mode || 'backtest',
          take_profit_stage: trade.take_profit_stage || trade.takeProfitStage || trade.tp_stage || undefined,
          take_profit_pct: trade.take_profit_pct || trade.takeProfitPct || trade.tp_pct || undefined,
          take_profit_amount: trade.take_profit_amount || trade.takeProfitAmount || trade.tp_amount || undefined,
          position_size: trade.position_size || trade.positionSize || trade.cost || undefined
        }));

        let totalProfitUsd = 0; let totalProfitPct = 0; let completedTrades = 0;
        let winningTrades = 0; let losingTrades = 0; let buyOrders = 0; let sellOrders = 0;
        let buyWins = 0; let buyLosses = 0; let sellWins = 0; let sellLosses = 0;
        let totalWinAmount = 0; let totalLossAmount = 0; let bestTradePct = -Infinity;
        let worstTradePct = Infinity; let grossProfitUsd = 0; let grossLossUsd = 0;

        (tradesRows || []).forEach((trade) => {
          const side = trade.side || '';
          const positionType = getPositionTypeFromSide(side, trade.position_type);
          const isLong = positionType === 'LONG';

          const isLongEntry = side === 'BUY' && isLong;
          const isShortEntry = side === 'SHORT' && !isLong;
          const isLongExit = (side === 'SELL' || side === 'SELL_TP' || side === 'SELL_SL') && isLong;
          const isShortExit = (side === 'BUY_TP' || side === 'BUY_SL') && !isLong;

          if (isLongEntry) buyOrders++;
          else if (isShortEntry) sellOrders++;

          if (isLongExit || isShortExit) {
            completedTrades++;
            const pnlUsd = trade.pnl_usd || trade.profit || 0;
            const pnlPct = trade.pnl_percent || trade.roi || 0;

            totalProfitUsd += pnlUsd;
            totalProfitPct += pnlPct;

            if (pnlPct > bestTradePct) bestTradePct = pnlPct;
            if (pnlPct < worstTradePct) worstTradePct = pnlPct;

            if (pnlPct > 0) {
              winningTrades++;
              if (isLong) buyWins++; else sellWins++;
              totalWinAmount += Math.abs(pnlPct);
              grossProfitUsd += pnlUsd;
            } else {
              losingTrades++;
              if (isLong) buyLosses++; else sellLosses++;
              totalLossAmount += Math.abs(pnlPct);
              grossLossUsd += Math.abs(pnlUsd);
            }
          }
        });

        let winRate = completedTrades > 0 ? (winningTrades / completedTrades) * 100 : 0;
        let avgWin = winningTrades > 0 ? totalWinAmount / winningTrades : 0;
        let avgLoss = losingTrades > 0 ? totalLossAmount / losingTrades : 0;
        let profitFactor = totalLossAmount > 0 ? totalWinAmount / totalLossAmount : totalWinAmount > 0 ? 999 : 0;

        if (bestTradePct === -Infinity) bestTradePct = 0;
        if (worstTradePct === Infinity) worstTradePct = 0;

        const tradesHaveValidPnl = grossProfitUsd > 0 || grossLossUsd > 0;

        if (!tradesHaveValidPnl) {
          const hasDbMetrics = (header.winning_trades > 0 || header.losing_trades > 0) || (header.avg_win_pct > 0 || header.gross_profit_usd > 0);
          if (hasDbMetrics) {
            completedTrades = header.total_trades || 0;
            winningTrades = header.winning_trades || 0;
            losingTrades = header.losing_trades || 0;
            winRate = header.win_rate || header.win_rate_pct || (completedTrades > 0 ? (winningTrades / completedTrades) * 100 : 0);
            totalProfitUsd = header.total_profit_amount || header.total_profit || 0;
            avgWin = header.avg_win_pct || 0;
            avgLoss = header.avg_loss_pct || 0;
            bestTradePct = header.best_trade_pct || 0;
            worstTradePct = header.worst_trade_pct || 0;
            profitFactor = header.profit_factor || 0;
            grossProfitUsd = header.gross_profit_usd || 0;
            grossLossUsd = header.gross_loss_usd || 0;
          }
        }

        setCalculatedStats({
          totalProfitUsd, totalProfitPct, totalTrades: completedTrades, winningTrades, losingTrades,
          winRate, maxDrawdown: header.max_drawdown_pct || 0, sharpeRatio: header.sharpe_ratio || 0,
          buyOrders, sellOrders, buyWins, buyLosses, sellWins, sellLosses, avgWin, avgLoss, profitFactor,
          bestTrade: bestTradePct, worstTrade: worstTradePct, grossProfit: grossProfitUsd, grossLoss: grossLossUsd,
        });

        const pairMap = new Map<string, PairStats>();
        (tradesRows || []).forEach((trade) => {
          const symbol = trade.symbol;
          if (!pairMap.has(symbol)) {
            pairMap.set(symbol, {
              symbol, total_trades: 0, winning_trades: 0, losing_trades: 0, win_rate: 0, total_profit_pct: 0,
              total_profit_usd: 0, avg_profit_pct: 0, best_trade_pct: -Infinity, worst_trade_pct: Infinity,
              buy_orders: 0, sell_orders: 0, buy_wins: 0, buy_losses: 0, sell_wins: 0, sell_losses: 0,
            });
          }
          const stats = pairMap.get(symbol)!;
          if (trade.side === 'SELL') {
            stats.total_trades++; stats.sell_orders++;
            stats.total_profit_usd += trade.pnl_usd || 0; stats.total_profit_pct += trade.pnl_percent || 0;
            if ((trade.pnl_percent || 0) > 0) { stats.winning_trades++; stats.sell_wins++; } 
            else { stats.losing_trades++; stats.sell_losses++; }
            if ((trade.pnl_percent || 0) > stats.best_trade_pct) stats.best_trade_pct = trade.pnl_percent || 0;
            if ((trade.pnl_percent || 0) < stats.worst_trade_pct) stats.worst_trade_pct = trade.pnl_percent || 0;
          } else {
            stats.buy_orders++;
          }
        });

        const pairStatsArray = Array.from(pairMap.values()).map(stats => ({
          ...stats,
          win_rate: stats.total_trades > 0 ? (stats.winning_trades / stats.total_trades) * 100 : 0,
          avg_profit_pct: stats.total_trades > 0 ? stats.total_profit_pct / stats.total_trades : 0,
          best_trade_pct: stats.best_trade_pct === -Infinity ? 0 : stats.best_trade_pct,
          worst_trade_pct: stats.worst_trade_pct === Infinity ? 0 : stats.worst_trade_pct,
        }));

        if (!alive) return;
        setBacktest(header); setCardRow(cardRowData); setTrades(tradesRows ?? []); setPairStats(pairStatsArray);
      } catch (err: any) {
        if (!alive) return; setError(err);
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (id) load();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading backtest details...</div></div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"><p className="text-red-600 dark:text-red-400">{error.message}</p></div></DashboardLayout>;
  if (!backtest) return null;

  const curr = (cardRow?.quote_currency || backtest.quote_currency || 'USDT').toUpperCase();
  const initialBalance = cardRow?.initial_capital ?? backtest?.initial_capital ?? cardRow?.account_balance ?? backtest?.account_balance ?? 1000;
  const { pairsCount, pctLabel, profitPctText, profitUsdText, roeLabel, roeText, totalCostLabel, totalCostText, turnoverLabel, turnoverText, feesText } = computeCardKpis(cardRow ?? backtest, initialBalance);
  
  const pct = toNumOrNull(cardRow?.total_profit_pct_weighted) ?? toNumOrNull(backtest?.total_profit_pct);
  const totalTrades = backtest.total_trades ?? calculatedStats.totalTrades;
  const winRate = backtest.win_rate_pct ?? calculatedStats.winRate;
  const maxDrawdown = backtest.max_drawdown_pct ?? cardRow?.max_drawdown_pct ?? 0;
  const sharpeRatio = backtest.sharpe_ratio ?? cardRow?.sharpe_ratio ?? calculatedStats.sharpeRatio ?? 0;
  const winningTrades = statsRow?.sell_wins ?? calculatedStats.winningTrades;
  const losingTrades = statsRow?.sell_losses ?? calculatedStats.losingTrades;
  const st = calculatedStats;

  return (
    <DashboardLayout>
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 relative z-10 -m-8 mb-6">
          <div className="mb-4">
            <Button onClick={() => navigate('/backtests')} variant="outline" icon={<ArrowLeft className="w-4 h-4" />} iconPosition="left">
              Back to Backtests
            </Button>
          </div>
          <div><h1 className="text-2xl font-bold text-gray-900 dark:text-white">{backtest.title}</h1></div>
          <div className="flex gap-6 mt-6 border-b border-gray-200 dark:border-gray-700">
            <button onClick={() => setActiveTab('overview')} className={`pb-3 px-1 border-b-2 transition-colors ${activeTab === 'overview' ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>Overview</button>
            <button onClick={() => setActiveTab('trades')} className={`pb-3 px-1 border-b-2 transition-colors ${activeTab === 'trades' ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>Trades</button>
            <button onClick={() => setActiveTab('pairs')} className={`pb-3 px-1 border-b-2 transition-colors ${activeTab === 'pairs' ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}>Pairs</button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"><span>{pctLabel}</span><Tooltip content={KPI_TOOLTIPS.portfolioPct.content} position="top"><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></Tooltip></p>
                      <p className={`text-2xl font-bold mt-1 ${profitPctText === '-' || profitPctText === '0.00%' ? 'text-gray-400' : profitPctText.startsWith('-') ? 'text-red-600' : 'text-green-600'}`}>{profitPctText}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{profitUsdText}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Fees {feesText}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${(pct ?? 0) >= 0 ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}>
                      {(pct ?? 0) >= 0 ? <TrendingUp className="w-6 h-6 text-green-600" /> : <TrendingDown className="w-6 h-6 text-red-600" />}
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"><span>Total Trades</span><Tooltip content={KPI_TOOLTIPS.trades.content} position="top"><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></Tooltip></p>
                      <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{totalTrades}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{pairsCount} {pairsCount === 1 ? 'pair' : 'pairs'} tested</p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/20"><BarChart3 className="w-6 h-6 text-blue-600" /></div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"><span>Win Rate</span><Tooltip content={KPI_TOOLTIPS.winRate.content} position="top"><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></Tooltip></p>
                      <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{winRate.toFixed(1)}%</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{winningTrades != null ? `${winningTrades} winning trades` : 'N/A'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/20"><Target className="w-6 h-6 text-purple-600" /></div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2"><span>Max Drawdown</span><Tooltip content={KPI_TOOLTIPS.maxDrawdown.content} position="top"><Info className="h-3.5 w-3.5 text-gray-400 cursor-help" /></Tooltip></p>
                      <p className="text-2xl font-bold mt-1 text-red-600">{maxDrawdown.toFixed(2)}%</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Sharpe Ratio: {sharpeRatio.toFixed(2)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/20"><TrendingDown className="w-6 h-6 text-red-600" /></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trades' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trade History</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{trades.length} trades found</p>
              </div>
              {trades.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No trades found.</div>
              ) : (
                <div className="p-6">
                  <TradeHistoryTable trades={trades} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'pairs' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-6">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Pairs Performance Summary</h3>
              </div>
              <div className="p-6 text-gray-500">
                 Peržiūrėkite monetų informaciją aukščiau.
              </div>
            </div>
          )}
        </div>
    </DashboardLayout>
  );
}

export default BacktestDetailsPage;
export { BacktestDetailsPage };